import type {
  VesselEvaluationSort,
  VesselEvaluationVessel,
} from "../models/vessel-evaluation.models";
import {
  formatVesselEvaluationValue,
  VESSEL_EVALUATION_COLUMNS,
} from "../utils/vessel-evaluation-selectors.utils";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/solid";
import { TableVirtuoso, type TableComponents } from "react-virtuoso";
import { twMerge } from "tailwind-merge";

interface VesselEvaluationTableProps {
  onSort: (key: keyof VesselEvaluationVessel) => void;
  sort: VesselEvaluationSort;
  vessels: VesselEvaluationVessel[];
}

const VESSEL_EVALUATION_TABLE_COMPONENTS: TableComponents<VesselEvaluationVessel> = {
  Table: ({ children, ...props }) => (
    <table
      {...props}
      className="w-max min-w-full border-separate border-spacing-0 text-[11px]"
      data-test="vessel-evaluation-table"
    >
      {children}
    </table>
  ),
  TableHead: (props) => (
    <thead
      {...props}
      className="z-10 bg-gray-50 text-left text-[10px] text-gray-700 dark:bg-gray-900 dark:text-gray-300"
    />
  ),
  TableRow: ({ item, ...props }) => (
    <tr
      {...props}
      className="bg-white text-gray-800 hover:bg-primary-10 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-teal-950/50"
      data-test={`vessel-evaluation-row-${item.vesselName}`}
    />
  ),
};

const VesselEvaluationCells = ({ vessel }: { vessel: VesselEvaluationVessel }) => (
  <>
    {VESSEL_EVALUATION_COLUMNS.map((column) => {
      const displayValue = formatVesselEvaluationValue(vessel[column.key], column.format);

      return (
        <td
          key={column.key}
          className={twMerge(
            "max-w-[260px] whitespace-nowrap border-r border-gray-200 px-2.5 py-1 align-top dark:border-gray-700",
            column.key === "vesselName" && "font-semibold",
            column.key === "imo" && "font-mono tabular-nums",
            column.format === "number" && "text-right tabular-nums",
          )}
          title={displayValue}
        >
          {displayValue}
        </td>
      );
    })}
  </>
);

// Keeps all 59 PBIP projections scrollable while mounting only the visible vessel rows.
const VesselEvaluationTable = ({ onSort, sort, vessels }: VesselEvaluationTableProps) => {
  if (vessels.length === 0) {
    return (
      <p className="px-4 py-12 text-center text-sm text-gray-500" data-test="vessel-evaluation-empty">
        No vessels match the selected filters.
      </p>
    );
  }

  return (
    <div
      className="h-[calc(100vh-300px)] min-h-[280px] max-h-[690px]"
      data-test="vessel-evaluation-table-scroll"
    >
      <TableVirtuoso
        data={vessels}
        fixedItemHeight={25}
        defaultItemHeight={25}
        overscan={250}
        computeItemKey={(_, vessel) => vessel.imo}
        components={VESSEL_EVALUATION_TABLE_COMPONENTS}
        fixedHeaderContent={() => (
          <tr>
            {VESSEL_EVALUATION_COLUMNS.map((column) => {
              const isActiveSort = sort.key === column.key;

              return (
                <th key={column.key} className="whitespace-nowrap border-b border-r border-gray-300 p-0 font-semibold dark:border-gray-700">
                  <button
                    type="button"
                    onClick={() => onSort(column.key)}
                    className="flex w-full items-center gap-1 px-2.5 py-2.5 text-left hover:bg-primary-10 dark:hover:bg-teal-950/50"
                    data-test={`vessel-evaluation-sort-${column.key}`}
                  >
                    <span className="max-w-[145px] whitespace-normal leading-3">{column.label}</span>
                    {isActiveSort && (
                      sort.direction === "ascending"
                        ? <ChevronUpIcon className="h-3 w-3 flex-none" />
                        : <ChevronDownIcon className="h-3 w-3 flex-none" />
                    )}
                  </button>
                </th>
              );
            })}
          </tr>
        )}
        itemContent={(_, vessel) => <VesselEvaluationCells vessel={vessel} />}
      />
    </div>
  );
};

export default VesselEvaluationTable;
